/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorC5,
	ResumeIndicatorC5Data,
} from "@/services/esus/indicator/esf/c5/getResumeIndicatorC5";
import {
	getResumeIndicatorC5ByHealthTeam,
	ResumeIndicatorC5ByHealthTeamData,
} from "@/services/esus/indicator/esf/c5/getResumeIndicatorC5ByHealthTeam";
import {
	DetailedCitizenScoresIndicatorC5Data,
	getDetailedCitizensScoresIndicatorC5,
} from "@/services/esus/indicator/esf/c5/getDetailedCitizensScoresIndicatorC5";
import {
	DetailedCitizenAcsVisitsIndicatorC5Data,
	getDetailedCitizensAcsVisitsIndicatorC5,
} from "@/services/esus/indicator/esf/c5/getDetailedCitizensAcsVisitsIndicatorC5";
import {
	DetailedCitizenAttendancesIndicatorC5Data,
	getDetailedCitizensAttendancesIndicatorC5,
} from "@/services/esus/indicator/esf/c5/getDetailedCitizensAttendancesIndicatorC5";

import { IndicatorEsfC5Resume } from "./IndicatorEsfC5Resume";
import { IndicatorEsfC5CitizensAttendances } from "./IndicatorEsfC5CitizensAttendances";
import { IndicatorEsfC5HealthTeams } from "./IndicatorEsfC5HealthTeams";
import { IndicatorEsfC5CitizensScores } from "./IndicatorEsfC5CitizensScores";
import { IndicatorEsfC5CitizensAcsVisits } from "./IndicatorEsfC5CitizensAcsVisits";

type IndicatorEsfC5Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEsfC5({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEsfC5Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorC5, setResumeIndicatorC5] = useState<ResumeIndicatorC5Data>(
		new ResumeIndicatorC5Data()
	);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorC5ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	const [pageNumberCitizensScores, setPageNumberCitizensScores] = useState(1);
	const [searchCitizensScore, setSearchCitizensScore] = useState("");
	const [isEvaluatedCitizensScore, setIsEvaluatedCitizensScore] = useState<boolean>();
	const debouncedSearchCitizensScore = useDebounce(searchCitizensScore, 2000);
	const [paginatedCitizensScores, setPaginatedCitizensScores] = useState(
		new Paginated<DetailedCitizenScoresIndicatorC5Data>()
	);
	const [isLoadingCitizensScores, setIsLoadingCitizensScores] = useState(false);

	const [pageNumberAttendances, setPageNumberAttendances] = useState(1);
	const [searchCitizensWithAttendance, setSearchCitizensWithAttendance] = useState("");
	const [isEvaluatedCitizensWithAttendance, setIsEvaluatedCitizensWithAttendance] =
		useState<boolean>();
	const debouncedSearchAttendance = useDebounce(searchCitizensWithAttendance, 2000);
	const [paginatedCitizensWithAttendances, setPaginatedCitizensWithAttendances] = useState(
		new Paginated<DetailedCitizenAttendancesIndicatorC5Data>()
	);
	const [isLoadingAttendances, setIsLoadingAttendances] = useState(false);

	const [pageNumberCitizensAcsVisits, setPageNumberCitizensAcsVisits] = useState(1);
	const [searchCitizensAcsVisit, setSearchCitizensAcsVisit] = useState("");
	const [isEvaluatedCitizensAcsVisit, setIsEvaluatedCitizensAcsVisit] = useState<boolean>();
	const debouncedSearchCitizensAcsVisit = useDebounce(searchCitizensAcsVisit, 2000);
	const [paginatedCitizensAcsVisits, setPaginatedCitizensAcsVisits] = useState(
		new Paginated<DetailedCitizenAcsVisitsIndicatorC5Data>()
	);
	const [isLoadingCitizensAcsVisits, setIsLoadingCitizensAcsVisits] = useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorC5(),
			handleGetResumeIndicatorC5ByHealthTeam(),
			handleGetDetailedCitizensScoresIndicatorC5(),
			handleGetDetailedCitizensWithAttendancesIndicatorC5(),
			handleGetDetailedCitizensAcsVisitsIndicatorC5(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorC5() {
		setResumeIndicatorC5(
			await getResumeIndicatorC5({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorC5ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorC5ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorC5ByHealthTeam() {
		return (
			await getResumeIndicatorC5ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensScoresIndicatorC5(pageNumber?: number) {
		setIsLoadingCitizensScores(true);
		setPaginatedCitizensScores(
			await getDetailedCitizensScoresIndicatorC5({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensScore,
				isEvaluated: isEvaluatedCitizensScore,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensScores(false);
	}

	async function handleGetAllDetailedCitizensScoresIndicatorC5() {
		return (
			await getDetailedCitizensScoresIndicatorC5({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensScore,
				isEvaluated: isEvaluatedCitizensScore,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensWithAttendancesIndicatorC5(pageNumber?: number) {
		setIsLoadingAttendances(true);
		setPaginatedCitizensWithAttendances(
			await getDetailedCitizensAttendancesIndicatorC5({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensWithAttendance,
				isEvaluated: isEvaluatedCitizensWithAttendance,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingAttendances(false);
	}

	async function handleGetAllDetailedCitizensWithAttendancesIndicatorC5() {
		return (
			await getDetailedCitizensAttendancesIndicatorC5({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensWithAttendance,
				isEvaluated: isEvaluatedCitizensWithAttendance,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensAcsVisitsIndicatorC5(pageNumber?: number) {
		setIsLoadingCitizensAcsVisits(true);
		setPaginatedCitizensAcsVisits(
			await getDetailedCitizensAcsVisitsIndicatorC5({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensAcsVisit,
				isEvaluated: isEvaluatedCitizensAcsVisit,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensAcsVisits(false);
	}

	async function handleGetAllDetailedCitizensAcsVisitsIndicatorC5() {
		return (
			await getDetailedCitizensAcsVisitsIndicatorC5({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensAcsVisit,
				isEvaluated: isEvaluatedCitizensAcsVisit,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorC5ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorC5ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensScoresIndicatorC5(pageNumberCitizensScores);
	}, [pageNumberCitizensScores]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensScores === 1) {
				handleGetDetailedCitizensScoresIndicatorC5(pageNumberCitizensScores);
			} else {
				setPageNumberCitizensScores(1);
			}
		}
	}, [debouncedSearchCitizensScore, isEvaluatedCitizensScore]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensWithAttendancesIndicatorC5(pageNumberAttendances);
	}, [pageNumberAttendances]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberAttendances === 1) {
				handleGetDetailedCitizensWithAttendancesIndicatorC5(pageNumberAttendances);
			} else {
				setPageNumberAttendances(1);
			}
		}
	}, [debouncedSearchAttendance, isEvaluatedCitizensWithAttendance]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensAcsVisitsIndicatorC5(pageNumberCitizensAcsVisits);
	}, [pageNumberCitizensAcsVisits]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensAcsVisits === 1) {
				handleGetDetailedCitizensAcsVisitsIndicatorC5(pageNumberCitizensAcsVisits);
			} else {
				setPageNumberCitizensAcsVisits(1);
			}
		}
	}, [debouncedSearchCitizensAcsVisit, isEvaluatedCitizensAcsVisit]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("C5 - Cuidado da Pessoa com Hipertensão");
	}, [setTitle]);

	return (
		<>
			<IndicatorEsfC5Resume resume={resumeIndicatorC5} />
			<IndicatorEsfC5HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorC5ByHealthTeam={
					handleGetAllResumeIndicatorC5ByHealthTeam
				}
			/>
			<IndicatorEsfC5CitizensScores
				paginatedCitizensScores={paginatedCitizensScores}
				isLoading={isLoadingCitizensScores}
				search={searchCitizensScore}
				setSearch={setSearchCitizensScore}
				isEvaluated={isEvaluatedCitizensScore}
				setIsEvaluated={setIsEvaluatedCitizensScore}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensScores}
				setPageNumber={setPageNumberCitizensScores}
				handleGetAllDetailedCitizensScoresIndicatorC5={
					handleGetAllDetailedCitizensScoresIndicatorC5
				}
			/>
			<IndicatorEsfC5CitizensAttendances
				paginatedAttendances={paginatedCitizensWithAttendances}
				isLoading={isLoadingAttendances}
				search={searchCitizensWithAttendance}
				setSearch={setSearchCitizensWithAttendance}
				isEvaluated={isEvaluatedCitizensWithAttendance}
				setIsEvaluated={setIsEvaluatedCitizensWithAttendance}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberAttendances}
				setPageNumber={setPageNumberAttendances}
				handleGetAllDetailedIndicatorC5={
					handleGetAllDetailedCitizensWithAttendancesIndicatorC5
				}
			/>
			<IndicatorEsfC5CitizensAcsVisits
				paginatedCitizensAcsVisits={paginatedCitizensAcsVisits}
				isLoading={isLoadingCitizensAcsVisits}
				search={searchCitizensAcsVisit}
				setSearch={setSearchCitizensAcsVisit}
				isEvaluated={isEvaluatedCitizensAcsVisit}
				setIsEvaluated={setIsEvaluatedCitizensAcsVisit}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensAcsVisits}
				setPageNumber={setPageNumberCitizensAcsVisits}
				handleGetAllDetailedCitizensAcsVisitsIndicatorC5={
					handleGetAllDetailedCitizensAcsVisitsIndicatorC5
				}
			/>
		</>
	);
}
